import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

/**
 * Article body is Shopify RTE HTML:
 *   <p>copy</p>
 *   <blockquote><p>pull quote</p></blockquote>
 *   <p><img><img></p>          // 2-up photo grid
 *   <p><img><img><img></p>     // 3-up photo grid
 *   <p><img></p>               // full-width photo
 *
 * Images live inside the same <p> with no leftover text, so we promote
 * those paragraphs into `.article-body__media-grid` before styling/animating.
 * Pull quotes are faded as a whole — SplitText wrapping words inside
 * blockquote > p is what made the "flashes" collapse.
 */
export default class ArticleBody {
  constructor(container) {
    this.container = container;
    this.content = container.querySelector('.article-body__content');
    if (!this.content) return;

    this.layoutMedia();
    this.initCopy();
    this.initQuotes();
    this.initMedia();
  }

  layoutMedia = () => {
    Array.from(this.content.children).forEach((block) => {
      const images = Array.from(block.querySelectorAll('img'));
      if (images.length === 0) return;

      const leftover = block.cloneNode(true);
      leftover.querySelectorAll('img').forEach((img) => img.remove());
      if (leftover.textContent.replace(/\s+/g, '').length > 0) return;

      const count = Math.min(images.length, 4);
      const grid = document.createElement('div');
      grid.className = `article-body__media-grid article-body__media-grid--${count}`;

      images.forEach((img) => {
        const figure = document.createElement('figure');
        figure.className = 'article-body__media-item';
        figure.appendChild(img);
        grid.appendChild(figure);
      });

      block.replaceWith(grid);
    });
  };

  initCopy = () => {
    const copy = this.content.querySelectorAll(':scope > p, :scope > h2, :scope > h3');
    if (!copy.length) return;

    gsap.set(copy, { opacity: 0, y: 20 });
    ScrollTrigger.batch(copy, {
      start: 'top 90%',
      once: true,
      onEnter: (batch) => {
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
          stagger: { each: 0.08, from: 'start' },
        });
      },
    });
  };

  initQuotes = () => {
    this.content.querySelectorAll(':scope > blockquote').forEach((quote) => {
      if (!quote.textContent.trim()) {
        quote.hidden = true;
        return;
      }

      gsap.fromTo(
        quote,
        { opacity: 0, y: 28 },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: 'power3.out',
          scrollTrigger: { trigger: quote, start: 'top 85%', once: true },
        }
      );
    });
  };

  initMedia = () => {
    const grids = this.content.querySelectorAll('.article-body__media-grid');
    if (!grids.length) return;

    gsap.set(grids, { opacity: 0, y: 40 });
    ScrollTrigger.batch(grids, {
      start: 'top 90%',
      once: true,
      onEnter: (batch) => {
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: 'power3.out',
          stagger: { each: 0.08 },
        });
      },
    });
  };
}
